import { TourConfig } from '../models/tour-config.model';
import { TourFeature } from '../config/tour-feature.enum';
import { Audience } from '../config/audience.enum';
import { TourTrigger } from '../config/trigger.enum';
import { AnchorId } from '../config/anchor.registry';
import { planningReadinessBridge } from '../services/planning-readiness-bridge';

/**
 * MENTOR — Einstieg (AP-1 + AP-6, hier AP-1-Stand).
 *
 * Ersetzt das abgeloeste Zwei-Schritte-Onboarding (ShiftPlanning +
 * StaffingManagement, in AP-0 aus dem Funnel genommen). Diese Tour ist der
 * Einstieg in die Planung und beginnt mit dem **G0-Pre-Flight**: BEVOR der
 * Rundgang startet, prueft sie die Planungs-Voraussetzungen und leitet bei
 * Luecken gezielt zum Fix — statt ein fuehrungsloses leeres Raster zu zeigen
 * (Bruchstellen B1/B2/B4 aus EMPTY-ACCOUNT-REALITY.html).
 *
 * Datenquelle: `PlanningReadinessService` (vom TourCoordinator vor dem Start
 * vorgeladen) → Snapshot via `planningReadinessBridge.snapshot()`. Jeder
 * G0-Step skippt, sobald sein Signal erfuellt ist; auf einem vollstaendig
 * eingerichteten Konto entfaellt damit das gesamte Pre-Flight und es bleibt nur
 * der Willkommens-Schritt (W1-Platzhalter — die volle Verzweigung folgt in AP-6).
 */

/** Snapshot-Helfer fuer die skipIf/content/inlineChips-Closures. */
function snap() {
  return planningReadinessBridge.snapshot();
}

/**
 * True, solange noch mindestens eine Grundlagen-Lücke offen ist. Bewusst die
 * **Vereinigung der vier G0-Pre-Flight-Bedingungen** (nicht `snapshot.isReady`,
 * das partielle Orphans uebersieht) — jede dieser Luecken laesst die Planung
 * mit fehlenden Daten ins Leere laufen:
 *   - keine Abteilung
 *   - ≥1 Mitarbeiter ohne Abteilung (auch wenn andere zugeordnet sind)
 *   - Abteilung(en) vorhanden, aber keine Qualifikation verknuepft
 *   - Mitarbeiter vorhanden, aber keiner benachrichtigbar
 *
 * Quelle fuer den konsolidierenden W1-Gate-Schritt (`mentor-w1-gate`): solange
 * das `true` ist, fasst der Mentor die offenen Punkte am Ende noch einmal
 * zusammen und gated den Einstieg in die Planung. Sobald der User die Luecken
 * abgearbeitet hat, wird `false` → der Gate-Schritt entfaellt und der normale
 * „Womit anfangen?"-Chooser (`mentor-w1`) erscheint. So passt sich die Reise
 * datengetrieben an (User-Wunsch „Smartness/Agilitaet je nach Datenfuelle").
 */
function hasSetupGap(s: ReturnType<typeof snap>): boolean {
  if (!s) return false;
  return (
    !s.hasDepartment ||
    s.orphanEmployees.length > 0 ||
    (s.hasDepartment && !s.hasLinkedSkill) ||
    (s.totalActiveEmployees > 0 && !s.hasNotifiableEmployee)
  );
}

export const MENTOR_ENTRY_TOUR: TourConfig = {
  id: TourFeature.MentorEntry,
  version: 1,
  title: 'ONBOARDING_CHECKLIST.TOUR.MENTOR_ENTRY.TITLE',
  description: 'ONBOARDING_CHECKLIST.TOUR.MENTOR_ENTRY.DESC',
  audience: [Audience.Admin, Audience.TeamLeader],
  // Nur Planer sehen den Mentor (gleiche Stufen wie die abgeloeste
  // ShiftPlanning-Tour).
  requiredPermissions: [
    [
      'Shift.CanCreateShifts',
      'Shift.CanCreateTeamShifts',
      'Shift.CanCreateAllShifts',
    ],
  ],
  // Auto bei erstem Planer-Besuch (ersetzt die alte Dashboard-Kette) + jederzeit
  // manuell ueber "?"/Checkliste. Resume haelt den Fortschritt nach einem Fix.
  triggers: [TourTrigger.FirstVisit, TourTrigger.Manual, TourTrigger.Resume],
  triggerContext: {
    route: '/planner',
  },
  statePolicy: {
    allowReplay: true,
    snoozeEnabled: true,
    cooldownHoursAfterDismiss: 72,
    cooldownHoursAfterSnooze: 24,
    suppressAfterCompletion: true,
  },
  priority: 50,
  estimatedDurationSec: 150,
  category: 'setup',
  // G0-Steps entfallen modusabhaengig via skipIf → Zaehler nur sichtbare Steps.
  renumberVisibleSteps: true,
  // Mentor-Entry MUSS im 👤-Mitarbeiter-Modus starten: die G0-/W-1-Schritte
  // sind mitarbeiter-zentriert ("Wer soll arbeiten?", Orphan-Mitarbeiter) und
  // brauchen die Personen-Spalte links. Ohne expliziten `?view=mitarbeiter`
  // erbt der Planer den zuletzt gespeicherten Modus (localStorage
  // `planner.viewMode`) — landet der User aus einer Vorsession im ▦-Muster-
  // Modus, zeigt die leere Muster-Ansicht keine Mitarbeiter und der erste
  // Schritt laeuft ins Leere. Den Wechsel auf ▦ uebernimmt erst die
  // Muster-Zweigtour (mentor-pattern: `?view=muster`) nach der W1-Wahl.
  launchContext: {
    route: '/planner?view=mitarbeiter',
  },
  chapters: [
    {
      id: 'mentor-entry',
      title: 'ONBOARDING_CHECKLIST.TOUR.MENTOR_ENTRY.TITLE',
      steps: [
        // ── G0 Pre-Flight ────────────────────────────────────────────────
        {
          // Keine Abteilung → ohne Bereich laesst sich nichts planen.
          id: 'g0-no-department',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.G0.NO_DEPARTMENT.TITLE',
          content: 'TUTORIAL.MENTOR.G0.NO_DEPARTMENT.CONTENT',
          placement: 'auto',
          tone: 'warning',
          showNextButton: true,
          skipIf: () => {
            const s = snap();
            // Schnellpfad (Muster mit Lücken existiert): ganzes Pre-Flight weg.
            return !s || s.hasDepartment || s.hasPatternWithGap;
          },
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_DEPARTMENT.ACTION',
              icon: 'fa-solid fa-sitemap',
              route: '/organization',
              variant: 'primary',
              awaitEvent: ['department.created', 'department.updated'],
            },
          ],
          resumeRecoveryStepId: 'mentor-welcome',
        },
        {
          // Mitarbeiter ohne Abteilung (inkl. Eigentuemer) → tauchen im Planer
          // nicht als planbare Person auf. Chips oeffnen den Edit-Dialog direkt.
          id: 'g0-orphan-employees',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.G0.ORPHANS.TITLE',
          content: () =>
            snap()?.everyoneOrphan
              ? 'TUTORIAL.MENTOR.G0.ORPHANS.CONTENT_ALL'
              : 'TUTORIAL.MENTOR.G0.ORPHANS.CONTENT_SOME',
          contentParams: () => {
            const s = snap();
            if (!s) return {};
            return {
              count: s.orphanEmployees.length,
              names: s.orphanEmployees.map((o) => o.name).join(', '),
            };
          },
          // Voice-Over folgt dem Tooltip-Zweig: Bei `everyoneOrphan` (wirklich
          // NIEMAND hat eine Abteilung) bleibt es bei der Basis-Spur
          // `g0-orphan-employees` — deren Text ist exakt der „niemand"-Fall.
          // Hat hingegen nur ein TEIL keine Abteilung, spielt die `-some`-Spur,
          // die klar differenziert („nicht alle"). Ohne Snapshot ist der Step
          // ohnehin via skipIf weg, daher ist die Basis ein sicherer Fallback.
          // Vorher lief in BEIDEN Fällen die „niemand"-Spur — fachlich falsch,
          // sobald mindestens ein Mitarbeiter bereits zugeordnet ist.
          audioStepId: () =>
            snap()?.everyoneOrphan
              ? 'g0-orphan-employees'
              : 'g0-orphan-employees-some',
          placement: 'auto',
          tone: 'warning',
          showNextButton: true,
          skipIf: () => {
            const s = snap();
            // Schnellpfad (Muster mit Lücken existiert): ganzes Pre-Flight weg.
            return !s || s.orphanEmployees.length === 0 || s.hasPatternWithGap;
          },
          inlineChips: () => {
            const s = snap();
            if (!s) return [];
            return s.orphanEmployees.map((o) => ({
              id: o.id,
              name: o.name,
              route: '/employee',
              queryParams: { editEmployee: o.id, editName: o.name },
            }));
          },
          inlineChipsLimit: 5,
          resumeRecoveryStepId: 'mentor-welcome',
        },
        {
          // Keine Qualifikation mit einer Abteilung verknuepft → die spaetere
          // Kandidaten-Sortierung beim Besetzen ist substanzlos. Nur zeigen,
          // wenn ueberhaupt schon eine Abteilung existiert.
          id: 'g0-no-linked-skill',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.G0.NO_SKILL.TITLE',
          content: 'TUTORIAL.MENTOR.G0.NO_SKILL.CONTENT',
          placement: 'auto',
          tone: 'warning',
          showNextButton: true,
          skipIf: () => {
            const s = snap();
            // Schnellpfad (Muster mit Lücken existiert): ganzes Pre-Flight weg.
            return !s || !s.hasDepartment || s.hasLinkedSkill || s.hasPatternWithGap;
          },
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_SKILL.ACTION',
              icon: 'fa-solid fa-award',
              // `/organization/skills`, nicht `/organization` (korrigiert 25.08.2026).
              // Dieser Step verlangt, eine QUALIFIKATION mit einer Abteilung zu
              // verknuepfen — das geht nur im Reiter „Qualifikationen".
              // `/organization` leitet per `redirectTo` auf `departments`
              // (organization-routing.module.ts:6-10); der User landete im
              // Abteilungen-Reiter und musste den richtigen selbst finden.
              // Der `:tab`-Parameter aktiviert den Reiter direkt, die Anchor
              // `skills-table`/`add-skill-button` existieren dort (geprueft).
              // Beim Step `g0-no-department` ist `/organization` dagegen KORREKT.
              route: '/organization/skills',
              variant: 'primary',
              awaitEvent: ['skill.created', 'skill.updated'],
            },
          ],
          resumeRecoveryStepId: 'mentor-welcome',
        },
        {
          // Es gibt Mitarbeiter, aber keinen mit Benachrichtigungskanal →
          // Freigeben wuerde niemanden erreichen. (Vertieft in AP-5/AP-8.)
          id: 'g0-no-notifiable',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.G0.NO_CHANNEL.TITLE',
          content: 'TUTORIAL.MENTOR.G0.NO_CHANNEL.CONTENT',
          placement: 'auto',
          tone: 'warning',
          showNextButton: true,
          skipIf: () => {
            const s = snap();
            // Schnellpfad (Muster mit Lücken existiert): ganzes Pre-Flight weg.
            return (
              !s ||
              s.totalActiveEmployees === 0 ||
              s.hasNotifiableEmployee ||
              s.hasPatternWithGap
            );
          },
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_CHANNEL.ACTION',
              icon: 'fa-solid fa-paper-plane',
              route: '/employee',
              variant: 'primary',
              awaitEvent: ['employee.created', 'employee.updated'],
            },
          ],
          resumeRecoveryStepId: 'mentor-welcome',
        },
        // ── W−1 Sicherheitsrahmen (umgedeutet: "kein Team zu stören") ─────
        {
          id: 'mentor-safety',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.SAFETY.TITLE',
          content: 'TUTORIAL.MENTOR.SAFETY.CONTENT',
          placement: 'auto',
          showNextButton: true,
          // Sicherheitsrahmen ist „Von vorn"-Sprache und wuerde den Schnellpfad
          // (Muster existiert schon) stoeren — bei aktivem Schnellpfad weg.
          skipIf: () => {
            const s = snap();
            return !!s && s.hasPatternWithGap;
          },
        },
        // ── W1-Gate: Grundlagen-Lücken konsolidiert ─────────────────────────
        // Letzter Schritt im „Von vorn"-Pfad, SOLANGE noch eine Grundlagen-
        // Lücke offen ist (siehe hasSetupGap). Fasst die offenen Punkte aus
        // dem G0-Pre-Flight noch einmal gebuendelt zusammen und gated den
        // Einstieg in die Planung: Mit fehlenden Daten (Mitarbeiter ohne
        // Abteilung, Abteilung ohne Qualifikation, niemand benachrichtigbar)
        // ergibt Planen keinen Sinn. Statt hier den „Womit anfangen?"-Chooser
        // (mentor-w1) zu zeigen, bietet dieser Schritt gezielt die Fix-Wege an
        // (Action-Buttons je nach offener Lücke via `visibleIf` + Orphan-Chips).
        // Sobald alle Lücken geschlossen sind, skippt der Gate-Schritt und
        // mentor-w1 uebernimmt — die Reise passt sich also datengetrieben an.
        // Schliesst sich mit `mentor-w1` ueber hasSetupGap gegenseitig aus.
        {
          id: 'mentor-w1-gate',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.W1_GATE.TITLE',
          content: 'TUTORIAL.MENTOR.W1_GATE.CONTENT',
          placement: 'auto',
          tone: 'warning',
          // Resume nach einem Fix: Wenn der User eine Lücke geschlossen hat und
          // KEINE mehr offen ist, ist dieser Gate-Step beim Wiedereinstieg nicht
          // mehr renderbar (skipIf greift). Dann direkt zum „Womit anfangen?"-
          // Chooser springen, statt ueber die Rueckwaerts-Heuristik wieder auf
          // mentor-safety zu landen. Sind noch Lücken offen, bleibt der Gate
          // renderbar und der Resume setzt hier fort (zeigt die Restpunkte).
          resumeRecoveryStepId: 'mentor-w1',
          // Kein „Weiter": der Gate ist bewusst ein Halt — weiter geht es erst
          // ueber das Schliessen einer Lücke (Action/Chip pausiert + navigiert,
          // Resume skippt den Gate dann). Zurueck bleibt erlaubt.
          showNextButton: false,
          skipIf: () => {
            const s = snap();
            // Nur im „Von vorn"-Pfad und nur solange eine Lücke offen ist.
            return !s || s.hasPatternWithGap || !hasSetupGap(s);
          },
          // Orphan-Mitarbeiter als Quick-Action-Chips (Klick → Edit-Dialog),
          // identisch zum g0-orphan-employees-Schritt.
          inlineChips: () => {
            const s = snap();
            if (!s) return [];
            return s.orphanEmployees.map((o) => ({
              id: o.id,
              name: o.name,
              route: '/employee',
              queryParams: { editEmployee: o.id, editName: o.name },
            }));
          },
          inlineChipsLimit: 5,
          // Fix-Buttons je offener Lücke. `visibleIf` spiegelt exakt die vier
          // G0-Pre-Flight-Bedingungen — es erscheinen nur die Buttons, deren
          // Lücke noch offen ist. Labels werden von den G0-Steps wieder-
          // verwendet (gleiche Wortwahl, keine neuen i18n-Keys noetig).
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_DEPARTMENT.ACTION',
              icon: 'fa-solid fa-sitemap',
              route: '/organization',
              variant: 'primary',
              // Round-Trip: nach dem Anlegen der Abteilung automatisch zurueck.
              awaitEvent: ['department.created', 'department.updated'],
              visibleIf: () => {
                const s = snap();
                return !!s && !s.hasDepartment;
              },
            },
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_SKILL.ACTION',
              icon: 'fa-solid fa-award',
              // Dieselbe Korrektur wie beim Step `g0-no-linked-skill` (25.08.2026):
              // der Qualifikations-Knopf muss in den Reiter „Qualifikationen"
              // fuehren, sonst landet der User im Abteilungen-Reiter (Redirect in
              // organization-routing.module.ts:6-10). Der Knopf direkt darueber
              // (NO_DEPARTMENT) bleibt bewusst auf `/organization`.
              route: '/organization/skills',
              variant: 'primary',
              // Round-Trip: nach dem Verknuepfen der Qualifikation zurueck.
              awaitEvent: ['skill.created', 'skill.updated'],
              visibleIf: () => {
                const s = snap();
                return !!s && s.hasDepartment && !s.hasLinkedSkill;
              },
            },
            {
              labelKey: 'TUTORIAL.MENTOR.G0.NO_CHANNEL.ACTION',
              icon: 'fa-solid fa-paper-plane',
              route: '/employee',
              variant: 'secondary',
              // Round-Trip: nach dem Hinterlegen einer E-Mail (employee-Save) zurueck.
              awaitEvent: ['employee.created', 'employee.updated'],
              visibleIf: () => {
                const s = snap();
                return (
                  !!s &&
                  s.totalActiveEmployees > 0 &&
                  !s.hasNotifiableEmployee
                );
              },
            },
          ],
        },
        // ── Schnellpfad: Muster existiert schon → direkt Lücken besetzen ───
        // Wenn bereits ein Wochenmuster MIT offenen Lücken laeuft (typischer
        // Fall nach „Tour abgebrochen + Fortsetzen"), ist der Muster-Bau-
        // Schritt erledigt. Statt den „Womit anfangen?"-Chooser (W1) zu zeigen
        // und den Wizard ein zweites Mal anzubieten, fuehren wir direkt in die
        // Lücken-Stufe (MentorReunite → Endgame). Dieser Schritt und mentor-w1
        // schliessen sich ueber `hasPatternWithGap` gegenseitig aus, sind also
        // nie beide sichtbar. Routing = Handoff (setup→feature-discovery), exakt
        // wie die W1-Karten — MentorEntry bleibt offen, kein faelschliches
        // „abgeschlossen".
        {
          id: 'mentor-w1-continue',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.W1B.TITLE',
          content: 'TUTORIAL.MENTOR.W1B.CONTENT',
          placement: 'auto',
          showNextButton: false,
          // Einziger sichtbarer Schritt im Schnellpfad → kein „Zurück" anbieten
          // (es gibt nichts dahinter; alle Vorgaenger skippen).
          showPrevButton: false,
          skipIf: () => {
            const s = snap();
            // Nur zeigen, wenn ein Muster MIT offenen Lücken existiert.
            return !s || !s.hasPatternWithGap;
          },
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.W1B.CTA_FILL',
              icon: 'fa-solid fa-arrow-right',
              // Direkt in den Endstrang (R1-Ernte-Reveal ist jetzt dessen
              // erster Schritt) — kein eigener MentorReunite-Zwischen-Tour mehr,
              // der bei „1/1" mit „Abschließen" zurücksprang.
              startTour: TourFeature.MentorEndgame,
              variant: 'primary',
            },
            {
              labelKey: 'TUTORIAL.MENTOR.W1B.CTA_NEW',
              icon: 'fa-solid fa-table-cells',
              startTour: TourFeature.MentorPattern,
              variant: 'secondary',
            },
            {
              labelKey: 'TUTORIAL.MENTOR.W1B.CTA_EXPLORE',
              icon: 'fa-solid fa-eye',
              startTour: TourFeature.MentorExplore,
              variant: 'secondary',
            },
          ],
        },
        // ── W1 Eingangsfrage (3-Karten-Weiche via actions[].startTour) ────
        // Jede Karte UEBERGIBT (Handoff) an den jeweiligen Zweig, schliesst
        // MentorEntry dabei aber NICHT ab: "Erste Planung" gilt erst als
        // erledigt, wenn ein echter Plan existiert (≥1 Wochenmuster ODER
        // ≥1 Schicht) — daten-getrieben im CompletionDetector. Den Handoff
        // (setup→nicht-setup → kein transitionCompleted/Toast) macht der
        // TourCoordinator (siehe „Handoff-Sonderfall" in runTourV2). So
        // markiert blosses Weg-Waehlen den Schritt nicht mehr faelschlich
        // als fertig. AP-3: bei 0 Mustern ist der Muster-Weg die empfohlene
        // (primaere) Karte, damit die Ernte-Buehne fuer R1/R2 entsteht.
        {
          id: 'mentor-w1',
          anchor: AnchorId.PlannerView,
          title: 'TUTORIAL.MENTOR.W1.TITLE',
          content: () =>
            snap()?.hasPattern
              ? 'TUTORIAL.MENTOR.W1.CONTENT'
              : 'TUTORIAL.MENTOR.W1.CONTENT_NO_PATTERN',
          placement: 'auto',
          showNextButton: false,
          // Zwei Gegenstuecke:
          //  1. Schnellpfad: Sobald ein Muster MIT Lücken existiert, uebernimmt
          //     mentor-w1-continue (direkt Lücken besetzen) — sonst muesste der
          //     Nutzer den Wizard-Weg ein zweites Mal anwaehlen.
          //  2. Grundlagen-Gate: Solange noch eine Setup-Lücke offen ist,
          //     uebernimmt mentor-w1-gate (offene Punkte zuerst beheben). Der
          //     „Womit anfangen?"-Chooser erscheint erst, wenn das Fundament
          //     steht — Planen mit fehlenden Daten ergibt keinen Sinn.
          // Bei fehlendem Snapshot (`!s`) zeigt der Chooser sicherheitshalber
          // (kein Gate ohne Daten).
          skipIf: () => {
            const s = snap();
            return !!s && (s.hasPatternWithGap || hasSetupGap(s));
          },
          actions: [
            {
              labelKey: 'TUTORIAL.MENTOR.W1.CARD_PATTERN',
              icon: 'fa-solid fa-table-cells',
              startTour: TourFeature.MentorPattern,
              variant: 'primary',
            },
            {
              labelKey: 'TUTORIAL.MENTOR.W1.CARD_SINGLE',
              icon: 'fa-solid fa-pen',
              startTour: TourFeature.MentorSingleShift,
              variant: 'secondary',
            },
            {
              labelKey: 'TUTORIAL.MENTOR.W1.CARD_EXPLORE',
              icon: 'fa-solid fa-eye',
              startTour: TourFeature.MentorExplore,
              variant: 'secondary',
            },
          ],
        },
      ],
    },
  ],
};
